import { useEffect, useState } from "react";
import {
	Container,
	SimpleGrid,
	Stack,
} from "@chakra-ui/react";

import { ITeamMemberHighlight, TeamMember } from "./TeamMember";
import { useCmsApi } from "../../services/useCmsApi";
import { ITeamMember } from "../../models/ICmsModels";

interface ITeamDisplay {
	name: string;
	title: string;
	avatarSrc?: string;
	highlights: ITeamMemberHighlight[];
	email?: string;
	phone?: string;
	linkedIn?: string;
}

const fallbackTeam: ITeamDisplay[] = [
	{
		name: "Greg Stewart, PhD",
		title: "CEO, Founder",
		avatarSrc: "greg-stewart.jpg",
		highlights: [
			{ highlight: "Founder & CEO of Geco, developing tools that help farmers optimize weed management across the field using spatial data, predictive modelling, and targeted interventions.", subHighlights: [] },
			{ highlight: "Previously developed data and dynamic systems across several industrial sectors, with more than 20 years of experience bringing practical technologies to market.", subHighlights: [] },
		],
		linkedIn: "https://www.linkedin.com/in/greg-stewart-91780b/",
		email: "mailto:info@geco-ag.com",
		phone: "tel:+1-604-809-0430",
	},
	{
		name: "Devin Kirk, PhD",
		title: "Senior Data Scientist",
		avatarSrc: "devin-kirk.jpg",
		highlights: [
			{ highlight: "Specializes in ecological modelling, predictive analytics, and applied data science for agricultural systems.", subHighlights: [] },
			{ highlight: "Focused on building practical tools and insights that help improve field-level decision-making.", subHighlights: [] },
			{ highlight: "Proven track record applying modelling and analytics to real-world agricultural challenges.", subHighlights: [] },
		],
		email: "mailto:devin.kirk@geco-ag.com",
		linkedIn: "https://www.linkedin.com/in/devin-kirk-467409114/",
	},
];

const mapApiToDisplay = (m: ITeamMember): ITeamDisplay => {
	let highlights: ITeamMemberHighlight[] = [];
	if (m.highlights) {
		try {
			const parsed = JSON.parse(m.highlights) as string[];
			highlights = parsed.map((h) => ({ highlight: h, subHighlights: [] }));
		} catch {
			highlights = [{ highlight: m.highlights, subHighlights: [] }];
		}
	}
	return {
		name: m.name,
		title: m.role || "",
		avatarSrc: m.avatarUrl || undefined,
		highlights,
		email: m.email ? `mailto:${m.email}` : undefined,
		phone: m.phone ? `tel:${m.phone}` : undefined,
		linkedIn: m.linkedInUrl || undefined,
	};
};


export const Team = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [team, setTeam] = useState<ITeamDisplay[]>(fallbackTeam);

	useEffect(() => {
		let cancelled = false;
		cmsApi.getTeamMembers().then((members) => {
			if (!cancelled && members.length > 0) {
				setTeam(members.map(mapApiToDisplay));
			}
		});
		return () => { cancelled = true; };
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Container py={{ base: "16", md: "24" }}>
			<Stack spacing={{ base: "12", md: "16" }}>
				<SimpleGrid
					columns={{ base: 1, md: 2, lg: 3 }}
					columnGap="8"
					rowGap={{ base: "6", md: "8", lg: "16" }}
				>
					{team.map((m) => (
						<TeamMember
							key={m.name}
							name={m.name}
							title={m.title}
							avatarSrc={m.avatarSrc}
							highlights={m.highlights}
							linkedIn={m.linkedIn}
							email={m.email}
							phone={m.phone}
						/>
					))}
				</SimpleGrid>
			</Stack>
		</Container>
	);
};
